import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { invoiceTitle, periodRange } from './payroll';
import { money } from './rateEngine';

const header = (doc, title, subtitle) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, 14, 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(110);
  doc.text(subtitle, 14, 25);
  doc.setTextColor(0);
};

const TABLE_STYLE = { styles: { fontSize: 9 }, headStyles: { fillColor: [16, 122, 87] } };

const total = (list, k) => list.reduce((a, s) => a + s[k], 0);
const pct = (r) => (r ? `${+(r * 100).toFixed(2)}%` : '—');

/**
 * payroll_sheet.pdf — the GROSS pay sheet handed to the accountant, who finalizes payroll (source deductions,
 * net pay, remittances). Summary per driver, then each driver's manifests and adjustments.
 */
export const downloadPayrollSheet = (settlements, periodStart, cfg) => {
  const doc = new jsPDF();
  header(doc, 'Gross Pay Sheet — for payroll', `Pay period ${periodRange(periodStart, cfg).label} · generated ${new Date().toLocaleString('en-CA')}`);
  autoTable(doc, {
    ...TABLE_STYLE,
    startY: 32,
    head: [['Driver', 'ID', 'Type', 'Pieces', 'Manifest pay', 'Adjustments', 'Gross pay', 'Vacation accrued', 'Status']],
    body: settlements.map((s) => [
      s.driver.name,
      s.driver.id,
      s.employmentType === 'owner_operator' ? 'Owner-operator' : 'Employee',
      s.pieces,
      money(s.gross),
      money(s.adjTotal),
      money(s.grossPay),
      s.vacationRate ? `${money(s.vacationAccrued)} (${pct(s.vacationRate)})` : '—',
      s.status,
    ]),
    foot: [['Total', '', '', total(settlements, 'pieces'), money(total(settlements, 'gross')), money(total(settlements, 'adjTotal')), money(total(settlements, 'grossPay')), money(total(settlements, 'vacationAccrued')), '']],
  });

  settlements.forEach((s) => {
    autoTable(doc, {
      ...TABLE_STYLE,
      startY: doc.lastAutoTable.finalY + 8,
      head: [[`${s.driver.name} — detail`, 'Date', 'Pieces', 'Amount']],
      body: [
        ...s.lines.map((l) => [`Manifest ${l.route}${l.backdated ? ' (back-dated)' : ''}`, l.date, l.pieces, money(l.amount)]),
        ...s.adjustments.map((a) => [`Adjustment: ${a.label}`, '', '', money(Number(a.amount))]),
      ],
      foot: [['Gross pay', '', '', money(s.grossPay)]],
    });
  });

  doc.save(`payroll_sheet_${periodStart}.pdf`);
};

/** Same gross pay sheet as CSV, one row per manifest/adjustment, for the accountant's payroll software. */
export const downloadGrossPayCsv = (settlements, periodStart, cfg) => {
  const { from, to } = periodRange(periodStart, cfg);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [['Pay period start', 'Pay period end', 'Driver ID', 'Driver', 'Type', 'Line type', 'Reference', 'Date', 'Pieces', 'Amount', 'Vacation rate']];
  settlements.forEach((s) => {
    const base = [from, to, s.driver.id, s.driver.name, s.employmentType];
    s.lines.forEach((l) => rows.push([...base, l.backdated ? 'manifest (back-dated)' : 'manifest', l.route, l.date, l.pieces, l.amount.toFixed(2), s.vacationRate]));
    s.adjustments.forEach((a) => rows.push([...base, 'adjustment', a.label, '', '', Number(a.amount).toFixed(2), s.vacationRate]));
  });
  const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `gross_pay_${periodStart}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** Customer invoice for a week of posted manifests. */
export const downloadInvoice = (invoice, cfg) => {
  const doc = new jsPDF();
  const title = invoiceTitle(invoice);
  header(doc, title, `${invoice.customer} · Service period ${periodRange(invoice.periodStart, cfg).label} · issued ${new Date(invoice.issuedAt).toLocaleDateString('en-CA')}`);
  autoTable(doc, {
    ...TABLE_STYLE,
    startY: 32,
    head: [['Date', 'Route', 'Manifest', 'Pieces', 'Delivery', 'Accessorials', 'Amount']],
    body: invoice.lines.map((l) => [l.date, l.route, l.manifestId, l.pieces, money(l.base), money(l.accessorial), money(l.amount)]),
  });
  autoTable(doc, {
    ...TABLE_STYLE,
    startY: doc.lastAutoTable.finalY + 6,
    margin: { left: 110 },
    theme: 'plain',
    body: [['Subtotal', money(invoice.subtotal)], ...invoice.taxes.map((t) => [t.label || t.code, money(t.amount)]), ['Total', money(invoice.total)]],
    columnStyles: { 1: { halign: 'right' } },
    didParseCell: (d) => {
      if (d.row.index === invoice.taxes.length + 1) d.cell.styles.fontStyle = 'bold';
    },
  });
  doc.save(`${invoice.number}${invoice.revision ? `-R${invoice.revision}` : ''}.pdf`);
};
